import { useButton } from '@react-aria/button'
import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import { filterDOMProps, mergeProps } from '@react-aria/utils'
import cx from 'classnames'
import React, { forwardRef } from 'react'

import { ReactComponent as Chevron } from 'assets/icons/chevron-down__12.svg'
import { useObjectRef } from 'utils/useObjectRef'

import { ButtonProps } from './Types'

import Styles from './Button.module.scss'
import { DOMProps } from '@react-types/shared'

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, fRef) => {
  const ref = useObjectRef<HTMLButtonElement>(fRef)
  const { buttonProps, isPressed } = useButton(props, ref)
  const { hoverProps, isHovered } = useHover(props)
  const { isFocusVisible, focusProps } = useFocusRing()
  const {
    children,
    size = 'sm',
    variant = 'primary',
    iconClassName,
    frontIcon: FrontIcon,
    endIcon: EndIcon,
    isDisabled,
    className,
    title,
    withChevron,
    forceActive = false,
    ignoreHover = false,
    endIconClassName,
    fullWidth,
    ...finalProps
  } = props

  const iconOnly = React.Children.count(children) < 1 && !withChevron && (!!FrontIcon || !!EndIcon)

  return (
    <button
      title={title}
      {...mergeProps(
        filterDOMProps(finalProps),
        buttonProps,
        hoverProps,
        focusProps,
        (props.onPointerEnter
          ? { onPointerEnter: props.onPointerEnter, onPointerLeave: props.onPointerLeave }
          : {}) as DOMProps,
      )}
      className={cx(
        Styles.root,
        Styles[size],
        Styles[variant],
        {
          [Styles.hovered]: !ignoreHover && isHovered,
          // TODO: Check the possibility and maintainability of a toggleButton
          // TODO: Replace forceActive with aria-pressed
          [Styles.pressed]: isPressed || forceActive || props['aria-pressed'],
          [Styles.focused]: isFocusVisible,
          [Styles.disabled]: isDisabled,
          [Styles.withFrontIcon]: !iconOnly && !!FrontIcon,
          [Styles.withEndIcon]: !iconOnly && !!EndIcon,
          [Styles.fullWidth]: fullWidth,
        },
        className,
      )}
      ref={ref}
    >
      {FrontIcon && <FrontIcon className={cx(Styles.frontIcon, iconClassName)} aria-hidden="true" />}
      {children && <span className={Styles.label}>{children}</span>}
      {EndIcon && <EndIcon className={cx(Styles.endIcon, iconClassName, endIconClassName)} aria-hidden="true" />}
      {!!withChevron && <Chevron className={cx(Styles.chevron, Styles[withChevron])} aria-hidden="true" />}
    </button>
  )
})

Button.displayName = 'Button'

export { Button }
